import React from 'react';
import { Play, Pause, X, Maximize2, ListChecks } from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useRoutineSessionStore } from '../stores/routineSessionStore';
import { formatPoints } from '../utils/levelCalculation';

/**
 * Slim header pill for a minimized routine run. Mirrors HabitSessionWidget: it
 * renders the live snapshot the running RoutineTimer publishes to the session
 * store, and drives pause / expand / stop from there.
 */
export function RoutineSessionWidget() {
  const runtime = useRoutineSessionStore(s => s.runtime);
  const isPaused = useRoutineSessionStore(s => s.isPaused);
  const togglePause = useRoutineSessionStore(s => s.togglePause);
  const expand = useRoutineSessionStore(s => s.expand);
  const requestStop = useRoutineSessionStore(s => s.requestStop);
  const { getCategoryColor } = useCategories();

  if (!runtime) return null;

  const color = getCategoryColor(runtime.category);

  const formatTime = (seconds: number) => {
    const s = Math.max(0, seconds);
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="flex w-fit max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 cursor-pointer hover:brightness-110 transition-all"
      style={{ backgroundColor: `${color}14`, borderColor: color }}
      onClick={expand}
    >
      <span
        className="flex items-center justify-center rounded-full p-1 shrink-0"
        style={{ backgroundColor: `${color}26` }}
      >
        <ListChecks className="w-3 h-3" style={{ color }} />
      </span>

      <span className="text-xs font-semibold text-foreground truncate max-w-[96px]">
        {runtime.stepLabel}
      </span>
      <span className="text-muted-foreground text-xs">·</span>
      <span className="font-mono text-xs text-foreground tabular-nums whitespace-nowrap">
        {formatTime(runtime.timeRemaining)}
      </span>
      <span className="text-muted-foreground text-xs">·</span>
      <span className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
        {formatPoints(runtime.earnedXP)} XP
      </span>

      <button
        onClick={(e) => { e.stopPropagation(); togglePause(); }}
        className="ml-0.5 p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title={isPaused ? 'Resume' : 'Pause'}
      >
        {isPaused ? (
          <Play className="w-3.5 h-3.5" style={{ color }} />
        ) : (
          <Pause className="w-3.5 h-3.5" style={{ color }} />
        )}
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); expand(); }}
        className="hidden sm:flex p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="Expand"
      >
        <Maximize2 className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); requestStop(); }}
        className="hidden sm:flex p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="Stop & finish"
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
